import type {RouteLocationNormalizedLoaded} from 'vue-router';
import type {FolhaComContexto, MenuItemFolha, MenuModulo} from './menu.type';

export function itemEhAtivo(item: MenuItemFolha, route: RouteLocationNormalizedLoaded): boolean {
    const destino = item.to;

    if (!destino) {
        return false;
    }

    if (typeof destino === 'string') {
        return route.path === destino;
    }

    return !!('name' in destino) && route.name === (destino as any).name;
}

export function moduloTemItemAtivo(modulo: MenuModulo, route: RouteLocationNormalizedLoaded): boolean {
    return modulo.subgrupos.some((subgrupo) => subgrupo.itens.some((item) => itemEhAtivo(item, route)));
}

export function normalizarTexto(texto: string): string {
    return texto
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

// Regra única de visibilidade (permissão) de um item — `visible` é preenchido em `menu.config.ts`.
export function itemVisivel(item: MenuItemFolha): boolean {
    return item.visible !== false;
}

export function obterTodasAsFolhas(modulos: MenuModulo[]): FolhaComContexto[] {
    const folhas: FolhaComContexto[] = [];

    modulos.forEach((modulo) => {
        modulo.subgrupos.forEach((subgrupo) => {
            subgrupo.itens
                .filter(itemVisivel)
                .forEach((item) => folhas.push({item, modulo, subgrupoTitulo: subgrupo.titulo}));
        });
    });

    return folhas;
}

// Nos textos secundários o termo precisa bater no início de uma palavra — senão termos curtos viram ruído
// ("nf" casaria com "co*nf*igure").
function algumaPalavraComecaCom(texto: string, termoNormalizado: string): boolean {
    const palavras = normalizarTexto(texto).split(/[^a-z0-9]+/);

    // Termo com espaço ("contas a pagar") compara com o texto corrido a partir de cada palavra.
    return palavras.some((_, indice) => palavras.slice(indice).join(' ').startsWith(termoNormalizado));
}

// 3 = nome começa com o termo · 2 = nome contém · 1 = palavra de módulo/subgrupo/descrição/palavra-chave
// começa com o termo · 0 = fora.
function pontuarFolha(folha: FolhaComContexto, termoNormalizado: string): number {
    const label = normalizarTexto(folha.item.label);

    if (label.startsWith(termoNormalizado)) {
        return 3;
    }

    if (label.includes(termoNormalizado)) {
        return 2;
    }

    const textosSecundarios = [
        folha.modulo.label,
        folha.subgrupoTitulo,
        folha.item.descricao,
        ...(folha.item.palavrasChave ?? [])
    ];

    return textosSecundarios.some((texto) => !!texto && algumaPalavraComecaCom(texto, termoNormalizado)) ? 1 : 0;
}

export function filtrarFolhasPorTermo(folhas: FolhaComContexto[], termo: string): FolhaComContexto[] {
    const termoNormalizado = normalizarTexto(termo.trim());

    if (!termoNormalizado) {
        return [];
    }

    // Sort estável: em empate de pontuação, mantém a ordem do menu.
    return folhas
        .map((folha) => ({folha, pontuacao: pontuarFolha(folha, termoNormalizado)}))
        .filter(({pontuacao}) => pontuacao > 0)
        .sort((a, b) => b.pontuacao - a.pontuacao)
        .map(({folha}) => folha);
}
